import { ArrowRightOutlined } from '@ant-design/icons';
import { HANDLING_PARTY_FILTER_SENTINELS } from '@shared/constants';
import { Input, InputNumber, Select } from 'antd';
import { UsersRound } from 'lucide-react';
import { memo, type ReactNode, useEffect, useRef, useState } from 'react';
import { operatorRequiresMultipleValues, operatorSupportsArrayValue } from '../core/operators';
import type { FieldConfig, FilterOperator, TextSearchFieldConfig } from '../core/types';
import { DateFilterValue } from './DateFilterValue';
import { getFilterPopupContainer } from './filter-popup-container';

interface FilterValueProps<TWhere = any> {
    value: any;
    operator: FilterOperator;
    fieldConfig: FieldConfig<any, TWhere> | TextSearchFieldConfig<TWhere>;
    whereClause?: TWhere;
    autoFocus?: boolean;
    onChange: (value: any) => void;
    onFocused?: () => void;
}

type SelectOption = { label: string; value: any; subtitle?: string };
type SelectOptionGroup = { label: ReactNode; title: string; options: SelectOption[] };

/** Uppercase section header rendered above a group of options. */
const filterOptionGroupHeader = (text: string): ReactNode => (
    <span className="font-semibold text-2xs text-subtle-foreground uppercase tracking-wider">{text}</span>
);

/** First letter of the first two words, e.g. "Holman Tolmie Insurance" → "HT". */
const optionInitials = (label: string): string =>
    label
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');

/**
 * Split a flat option list into "Relative to you" (the dynamic viewer-relative sentinel, e.g. the
 * handling-party "Mine") and a named group for the rest, so the sentinel reads as a distinct
 * concept rather than just another row. Returns the list unchanged when no sentinel is present.
 */
const buildRelativeOptionGroups = (
    options: SelectOption[],
    sentinel: string,
    restGroupLabel: string,
): (SelectOption | SelectOptionGroup)[] => {
    const relative = options.filter((option) => String(option.value) === sentinel);
    if (!relative.length) return options;
    const rest = options.filter((option) => String(option.value) !== sentinel);
    return [
        { label: filterOptionGroupHeader('Relative to you'), title: 'Relative to you', options: relative },
        ...(rest.length
            ? [{ label: filterOptionGroupHeader(restGroupLabel), title: restGroupLabel, options: rest }]
            : []),
    ];
};

/**
 * FilterValue Component
 *
 * Renders the appropriate input control based on field type and operator.
 * Handles all standard field types and special cases like range inputs,
 * multiselect, and async option loading.
 *
 * @component
 * @example
 * ```typescript
 * // Text input
 * <FilterValue
 *   value="john"
 *   operator="contains"
 *   fieldConfig={{ type: 'text', placeholder: 'Search name...' }}
 *   onChange={(val) => updateFilter(filterId, { value: val })}
 * />
 *
 * // Number range (between operator)
 * <FilterValue
 *   value={[10, 100]}
 *   operator="between"
 *   fieldConfig={{ type: 'number' }}
 *   onChange={(val) => updateFilter(filterId, { value: val })}
 * />
 *
 * // Async select options
 * <FilterValue
 *   value={['active', 'pending']}
 *   operator="in"
 *   fieldConfig={{
 *     type: 'multiselect',
 *     options: async () => fetchStatusOptions()
 *   }}
 *   onChange={(val) => updateFilter(filterId, { value: val })}
 * />
 * ```
 */
function FilterValueInner<TWhere = any>({
    value,
    operator,
    fieldConfig,
    whereClause,
    autoFocus,
    onChange,
    onFocused,
}: FilterValueProps<TWhere>) {
    // Handle async options loading
    const [options, setOptions] = useState<Array<{ label: string; value: any }>>(
        Array.isArray(fieldConfig.options) ? fieldConfig.options : [],
    );

    // Track if options have been loaded to prevent infinite loops
    const optionsLoadedRef = useRef(false);
    const lastFieldIdRef = useRef(fieldConfig.id);

    useEffect(() => {
        // Reset if field changed
        if (lastFieldIdRef.current !== fieldConfig.id) {
            optionsLoadedRef.current = false;
            lastFieldIdRef.current = fieldConfig.id;
        }

        // Handle static array options (sync update when field changes)
        if (Array.isArray(fieldConfig.options)) {
            setOptions(fieldConfig.options);
            return;
        }

        if (typeof fieldConfig.options === 'function' && !optionsLoadedRef.current) {
            optionsLoadedRef.current = true;

            // Check if the function accepts whereClause parameter
            const optionsFunc = fieldConfig.options;
            if (optionsFunc.length > 0) {
                // Function expects whereClause parameter
                optionsFunc(whereClause).then(setOptions).catch(console.error);
            } else {
                // Legacy function without parameters
                optionsFunc().then(setOptions).catch(console.error);
            }
        }
    }, [fieldConfig.id, fieldConfig.options, whereClause]);

    // Handle auto-focus callback
    useEffect(() => {
        if (autoFocus && onFocused) {
            // Small delay to ensure the element is fully rendered
            const timer = setTimeout(() => {
                onFocused();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [autoFocus, onFocused]);

    // Use custom render if provided
    if (fieldConfig.renderValue) {
        const CustomValue = fieldConfig.renderValue;
        return (
            <CustomValue
                value={value}
                onChange={onChange}
                operator={operator}
                autoFocus={autoFocus}
                whereClause={whereClause}
                options={options}
                getPopupContainer={getFilterPopupContainer}
            />
        );
    }

    // Handle different field types
    const fieldType = fieldConfig.type || 'text';

    // Text input
    if (fieldType === 'text') {
        return (
            <Input
                size="middle"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={fieldConfig.placeholder || 'Enter value...'}
                className="w-[120px] bg-transparent focus:bg-muted"
                autoFocus={autoFocus}
            />
        );
    }

    // Text search input
    if (fieldType === 'textSearch') {
        return (
            <div className="flex min-h-[38px] items-center">
                <Input.Search
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    onSearch={(val) => onChange(val)}
                    placeholder={fieldConfig.placeholder || 'Search...'}
                    className="crm-filter-value-search w-[200px] bg-transparent focus-within:bg-muted"
                    autoFocus={autoFocus}
                    allowClear
                />
            </div>
        );
    }

    // Number input
    if (fieldType === 'number' || fieldType === 'bigint') {
        if (operatorRequiresMultipleValues(operator)) {
            const [from, to] = Array.isArray(value) ? value : [null, null];
            return (
                <span className="inline-flex items-center gap-1">
                    <InputNumber
                        size="middle"
                        value={from}
                        onChange={(v) => onChange([v, to])}
                        placeholder="From"
                        className="w-[80px] bg-transparent"
                        autoFocus={autoFocus}
                    />
                    <ArrowRightOutlined className="text-subtle-foreground text-xs" />
                    <InputNumber
                        size="middle"
                        value={to}
                        onChange={(v) => onChange([from, v])}
                        placeholder="To"
                        className="w-[80px] bg-transparent"
                    />
                </span>
            );
        }

        return (
            <InputNumber
                size="middle"
                value={value}
                onChange={onChange}
                placeholder={fieldConfig.placeholder || '0'}
                className="min-w-[70px] bg-transparent px-1 focus:bg-muted"
                autoFocus={autoFocus}
            />
        );
    }

    // Date input — relative presets + custom DatePicker(s), see DateFilterValue.
    if (fieldType === 'date' || fieldType === 'datetime') {
        return (
            <DateFilterValue
                value={value}
                operator={operator}
                // Narrowed to a date field by the branch condition; the union includes textSearch.
                fieldConfig={fieldConfig as FieldConfig<any, TWhere>}
                onChange={onChange}
                autoFocus={autoFocus}
            />
        );
    }

    // Boolean input
    if (fieldType === 'boolean') {
        return (
            <Select
                size="middle"
                value={value}
                onChange={onChange}
                placeholder="Choose"
                options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ]}
                allowClear
                className="min-w-[90px]"
                autoFocus={autoFocus}
                getPopupContainer={getFilterPopupContainer}
            />
        );
    }

    // Select input
    if (fieldType === 'select' || fieldType === 'multiselect') {
        const isMultiple = fieldType === 'multiselect' || fieldConfig.multiple || operatorSupportsArrayValue(operator);

        // When the handling-party "Mine" sentinel is present, group it under "Relative to you" with
        // the real handling parties under "Organisations" — mirroring the user filter's "Me" grouping.
        const mineSentinel = String(HANDLING_PARTY_FILTER_SENTINELS.CURRENT_USER);
        const hasMineOption = options.some((option) => String(option.value) === mineSentinel);
        // No-op when the sentinel is absent, so non-handling-party selects stay flat.
        const renderedOptions = buildRelativeOptionGroups(options, mineSentinel, 'Organisations');
        // Read the subtitle from our own options state — antd drops custom fields from the option
        // passed to `optionRender` when options are grouped, so `option.subtitle` would be undefined.
        const mineSubtitle = (
            options.find((option) => String(option.value) === mineSentinel) as SelectOption | undefined
        )?.subtitle;

        return (
            <Select
                size="middle"
                mode={isMultiple ? 'multiple' : undefined}
                value={value}
                onChange={onChange}
                placeholder={fieldConfig.placeholder || 'Select...'}
                className="crm-filter-value-select min-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap"
                options={renderedOptions}
                optionRender={(option) => {
                    // Dynamic "Mine" sentinel: solid accent avatar + two-line card naming the
                    // viewer's own parties, matching the user filter's "Me" card.
                    if (String(option.value) === mineSentinel) {
                        return (
                            <span className="flex items-center gap-2.5 py-0.5">
                                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-accent-500">
                                    <UsersRound className="h-4 w-4 text-white" />
                                </span>
                                <span className="flex flex-col leading-tight">
                                    <span className="font-semibold text-accent-700">{option.label as ReactNode}</span>
                                    {mineSubtitle && (
                                        <span className="font-normal text-accent-600 text-xs">{mineSubtitle}</span>
                                    )}
                                </span>
                            </span>
                        );
                    }
                    // Other select filters keep their plain label; only the grouped handling-party
                    // organisations get a neutral initials badge to match the design.
                    if (!hasMineOption) return option.label as ReactNode;
                    const label = String(option.label);
                    return (
                        <span className="flex items-center gap-2.5">
                            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-separator bg-muted font-semibold text-muted-foreground text-xs">
                                {optionInitials(label)}
                            </span>
                            <span>{label}</span>
                        </span>
                    );
                }}
                maxTagCount={2}
                maxTagPlaceholder={(omittedValues) => (
                    <span className="text-muted-foreground">+{omittedValues.length}</span>
                )}
                allowClear
                showSearch
                optionLabelProp="label"
                popupMatchSelectWidth={false}
                popupClassName={hasMineOption ? 'crm-grouped-filter-popup' : undefined}
                filterOption={(input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                autoFocus={autoFocus}
                getPopupContainer={getFilterPopupContainer}
            />
        );
    }

    // Default to text input
    return (
        <Input
            size="middle"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={fieldConfig.placeholder || 'Enter value...'}
            className="min-w-[120px] border-none bg-transparent focus:bg-muted"
            autoFocus={autoFocus}
        />
    );
}

// Export memoized version without generics to avoid TypeScript issues with React.memo
export const FilterValue = memo(FilterValueInner) as typeof FilterValueInner;
